import { useEffect, useRef, useState } from 'react';
import {
  faChevronLeft,
  faFile,
  faFileVideo,
  faFolder,
  faImage,
  faMagnifyingGlass,
} from '@awesome.me/kit-4a6bf60416/icons/classic/regular';

import { useInfiniteScroll } from '@/components/asset-manager/hooks/useInfiniteScroll';
import type { Asset, Folder } from '@/components/asset-manager/types';
import { matchesAssetSearch } from '@/components/asset-manager/utils';
import { Button } from '@/components/ui/button';
import { TextInput } from '@/components/ui/form';
import { Icon } from '@/components/ui/icon';
import { MutedText, SmallText } from '@/components/ui/typography';
import type { AssetFileTypes } from '@/lib/assets-config';
import { pinItemToTop } from '@/lib/utils';
import { useAssetsStore } from '@/stores/assets-store';
import { ImageSelectorGrid } from './image-selector-grid';

export type LibraryFolderOpenRequest = {
  folderId: string;
};

interface LibraryFilesContentProps {
  selectedAssetId: string | null;
  pinnedAssetId?: string | null;
  pinnedFolderId?: string | null;
  folderOpenRequest?: LibraryFolderOpenRequest;
  onFolderOpenRequestHandled?: () => void;
  onAssetClick: (asset: Asset) => void;
  onClearSelection?: () => void;
  onAssetDoubleClick?: (asset: Asset) => void;
  fileTypes?: AssetFileTypes;
}

export function LibraryFilesContent({
  selectedAssetId,
  pinnedAssetId,
  pinnedFolderId,
  folderOpenRequest,
  onFolderOpenRequestHandled,
  onAssetClick,
  onClearSelection,
  onAssetDoubleClick,
  fileTypes = 'images',
}: LibraryFilesContentProps) {
  const {
    assets,
    folders,
    assetsByFolder,
    folderPagination,
    singleAssets,
    pagination,
    loadingFolderId,
    isLoading,
    isLoadingMore,
    error,
    loadAssets,
    loadFolderAssets,
    loadSingleAsset,
    loadMore,
    clearError,
  } = useAssetsStore();

  useEffect(() => {
    void loadAssets(fileTypes);
  }, [fileTypes, loadAssets]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentFolder, setCurrentFolder] = useState<Folder | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const resolvedPinnedFolderId =
    pinnedFolderId ?? (pinnedAssetId ? singleAssets[pinnedAssetId]?.folderId : undefined);

  const isFolderLoading = currentFolder
    ? !error && (loadingFolderId === currentFolder.id || !(currentFolder.id in assetsByFolder))
    : !error && isLoading;

  const activeAssets = currentFolder ? (assetsByFolder[currentFolder.id] ?? []) : assets;
  const showPinnedPreview = !isFolderLoading || activeAssets.length > 0;

  const activePagination = currentFolder ? folderPagination[currentFolder.id] : pagination;
  const hasNextPage =
    !searchQuery &&
    Boolean(activePagination && activePagination.currentPage < activePagination.totalPages);
  const handleLoadMore = () => loadMore(currentFolder?.id);

  const { sentinelRef } = useInfiniteScroll({
    hasNextPage,
    isLoading,
    onLoadMore: handleLoadMore,
    rootMargin: '100px',
    root: scrollContainerRef,
  });
  const handledFolderOpenRequest = useRef<LibraryFolderOpenRequest | null>(null);

  useEffect(() => {
    if (!folderOpenRequest) {
      handledFolderOpenRequest.current = null;
      return;
    }
    if (handledFolderOpenRequest.current === folderOpenRequest) return;
    const folder = folders.find((f) => f.id === folderOpenRequest.folderId) ?? null;
    if (!folder) return;
    handledFolderOpenRequest.current = folderOpenRequest;
    setCurrentFolder(folder);
    setSearchQuery('');
    onFolderOpenRequestHandled?.();
  }, [folderOpenRequest, folders, onFolderOpenRequestHandled]);

  // Auto-open the pinned folder when folders are available
  useEffect(() => {
    if (!resolvedPinnedFolderId) return;
    const folder = folders.find((f) => f.id === resolvedPinnedFolderId) ?? null;
    if (folder) setCurrentFolder(folder);
  }, [resolvedPinnedFolderId, folders]);

  useEffect(() => {
    if (currentFolder) {
      void loadFolderAssets(currentFolder.id, fileTypes);
    }
  }, [currentFolder, fileTypes, loadFolderAssets]);

  // Fetch the pinned asset individually if it's not in the currently loaded list.
  // This ensures it's always visible even when using infinite scroll pagination.
  // Skip when inside an unrelated folder — the asset doesn't belong there.
  // Skip at root when the asset belongs to a folder — it shouldn't appear at root level.
  useEffect(() => {
    if (!pinnedAssetId || !showPinnedPreview) return;

    const isInPinnedFolder = currentFolder?.id === resolvedPinnedFolderId;
    if (currentFolder && !isInPinnedFolder) return;
    if (!currentFolder && pinnedFolderId) return;
    if (!currentFolder && resolvedPinnedFolderId) return;

    const alreadyLoaded =
      activeAssets.some((a) => a.id === pinnedAssetId) || pinnedAssetId in singleAssets;
    if (!alreadyLoaded) {
      void loadSingleAsset(pinnedAssetId);
    }
  }, [
    pinnedAssetId,
    resolvedPinnedFolderId,
    currentFolder,
    activeAssets,
    singleAssets,
    loadSingleAsset,
    pinnedFolderId,
    showPinnedPreview,
  ]);

  const handleFolderOpen = (folderId: string) => {
    const folder = folders.find((f) => f.id === folderId) ?? null;
    setCurrentFolder(folder);
    setSearchQuery('');
    onClearSelection?.();
  };

  const handleBackToRoot = () => {
    setCurrentFolder(null);
    setSearchQuery('');
    onClearSelection?.();
    clearError();
  };

  // Inject the individually-fetched pinned asset at the top when it's not yet in the
  // paginated list. This avoids any re-ordering flicker once the page containing it loads.
  // Only inject inside the specific folder the asset belongs to — never bleed it into
  // unrelated folders or into the root view when the asset lives inside a folder.
  const activeAssetsWithPinned = (() => {
    if (!pinnedAssetId || !showPinnedPreview) return activeAssets;
    const isInPinnedFolder = currentFolder?.id === resolvedPinnedFolderId;
    if (currentFolder && !isInPinnedFolder) return activeAssets;

    if (!currentFolder && resolvedPinnedFolderId) return activeAssets;

    if (!currentFolder && pinnedFolderId) return activeAssets;

    const alreadyPresent = activeAssets.some((a) => a.id === pinnedAssetId);
    if (alreadyPresent) return activeAssets;

    const fetchedAsset = singleAssets[pinnedAssetId];
    if (!fetchedAsset) return activeAssets;

    return [fetchedAsset, ...activeAssets];
  })();

  const filteredAssets = pinItemToTop(
    activeAssetsWithPinned.filter((asset) => matchesAssetSearch(asset, searchQuery)),
    pinnedAssetId,
    (asset) => asset.id,
  );

  const loading = isFolderLoading && filteredAssets.length === 0;
  const isAllMode = fileTypes === 'all';
  const isVideoMode = fileTypes === 'videos';
  const assetLabelPlural = isAllMode ? 'assets' : isVideoMode ? 'videos' : 'images';
  const emptyStateIcon = isAllMode ? faFile : isVideoMode ? faFileVideo : faImage;

  return (
    <div className="flex flex-col h-full gap-4">
      {currentFolder && (
        <div className="flex items-center gap-1">
          <Button variant="link" size="sm" onClick={handleBackToRoot} className="h-auto gap-1 p-0">
            <Icon icon={faChevronLeft} className="size-3" />
            Back
          </Button>
          <SmallText className="text-muted-foreground">/ {currentFolder.name}</SmallText>
        </div>
      )}

      <TextInput
        leftSection={<Icon icon={faMagnifyingGlass} />}
        placeholder={`Search ${assetLabelPlural}...`}
        value={searchQuery}
        onChange={(e) => {
          setSearchQuery(e.target.value);
          onClearSelection?.();
        }}
      />

      {loading && (
        <div className="flex flex-1 items-center justify-center py-12">
          <MutedText>{`Loading ${assetLabelPlural}...`}</MutedText>
        </div>
      )}

      {error && (
        <div className="flex flex-1 items-center justify-center">
          <MutedText className="text-destructive">{error}</MutedText>
        </div>
      )}

      {!loading &&
        !error &&
        filteredAssets.length === 0 &&
        (currentFolder || folders.length === 0 || searchQuery) && (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 py-12">
            <Icon icon={emptyStateIcon} size="5x" className="text-muted-foreground" />
            <MutedText>
              {searchQuery
                ? `No ${assetLabelPlural} found matching your search`
                : `No ${assetLabelPlural} in library`}
            </MutedText>
          </div>
        )}

      {!loading &&
        !error &&
        (filteredAssets.length > 0 || (!currentFolder && !searchQuery && folders.length > 0)) && (
          <div ref={scrollContainerRef} className="flex-1 min-h-0 overflow-y-auto p-1">
            {!currentFolder && !searchQuery && folders.length > 0 && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 mb-4">
                {folders.map((folder) => (
                  <Button
                    key={folder.id}
                    variant="ghost"
                    onClick={() => handleFolderOpen(folder.id)}
                    className="h-[60px] w-full justify-start gap-3 bg-muted px-3 py-2 hover:bg-muted/70"
                  >
                    <div className="flex size-[40px] shrink-0 items-center justify-center bg-secondary">
                      <Icon icon={faFolder} className="ml-[1px] size-5 text-muted-foreground" />
                    </div>
                    <SmallText className="min-w-0 leading-tight truncate text-foreground">
                      {folder.name}
                    </SmallText>
                  </Button>
                ))}
              </div>
            )}

            {filteredAssets.length > 0 && (
              <ImageSelectorGrid
                assets={filteredAssets}
                selectedAssetId={selectedAssetId}
                onAssetClick={onAssetClick}
                onAssetDoubleClick={onAssetDoubleClick}
              />
            )}

            <div ref={sentinelRef} />
            {isLoadingMore && (
              <div className="flex justify-center py-4">
                <MutedText>Loading more...</MutedText>
              </div>
            )}
          </div>
        )}
    </div>
  );
}
